import { MultiStackedBarChart } from 'components/charts/MultiStackedBarChart';
import { FormSelect, FormSwitch } from 'components/form-fields';
import { ListingTable } from 'components/listing';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { parseBytesTo } from 'utils/parseBytesTo';

const DEFAULT_NO_OF_MONTHS = 12;

export function MonthlyStorage({ monthlyStorageData, loading }) {

    const [showAsPercentages, setShowAsPercentages] = useState(false);
    const [noOfMonthsToShow, setNoOfMonthsToShow] = useState(DEFAULT_NO_OF_MONTHS);

    const toMonthLabel = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', { month: 'short', year: 'numeric' });
    }

    const noOfMonths = [{ id: 12, name: 12 }, { id: 9, name: 9 }, { id: 6, name: 6 }, { id: 3, name: 3 }];

    const chartData = useMemo(() => {
        if (loading || !monthlyStorageData) {
            return [];
        }

        const transformedData = [];
        prepareChartData();
        populateChartData();

        return [...transformedData].sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime());

        function prepareChartData() {
            for (let i = 1; i <= noOfMonthsToShow; i++) {
                const date = new Date();
                date.setDate(1);
                date.setMonth(date.getMonth() - i);
                const monthLabel = toMonthLabel(date);
                const data = {
                    x: monthLabel,
                    series: [],
                    areas: []
                };

                transformedData.unshift(data);
            }
        }

        function populateChartData() {
            monthlyStorageData.forEach(item => {
                const itemMonthLabel = toMonthLabel(item.firstDateOfMonth);
                const x = transformedData.find(m => m.x === itemMonthLabel);
                if (x && item.totalSize > 0) {
                    x.series.push({
                        label: item.areaName,
                        value: showAsPercentages ? item.percentageOfTotalForMonth : parseFloat((item.totalSize / (1024 * 1024)).toFixed(2)),
                    });
                    x.areas.push([item.areaName, item.numberOfDocuments, parseBytesTo(item.totalSize).auto(), item.percentageOfTotalForMonth + '%']);
                }
            });
        }
    }, [monthlyStorageData, loading, noOfMonthsToShow, showAsPercentages]);

    const tooltipFormatter = (params) => {
        const lines = [
            `<b>${params[0]?.axisValue}</b>`,
            ...params.map((p) => `${p.marker}${p.seriesName}: ${p.value} ${showAsPercentages ? '%' : 'MB'}`),
        ];
        return lines.join("<br/>");
    };

    const controlPanel = useCallback(() => {
        return (<>
            <div className="chart-control">
                <span className="chart-control-label">MONTHS</span>
                <FormSelect items={noOfMonths} loading={loading} selectedValue={noOfMonthsToShow} dynamicWidth={true} onItemSelect={(value) => setNoOfMonthsToShow(value.name)}></FormSelect>
            </div>
            <div className="chart-control">
                <span className="chart-control-label">SHOW PERCENTAGES</span>
                <FormSwitch
                    checked={showAsPercentages}
                    onChange={(e) => {
                        setShowAsPercentages(e.target.checked);
                    }}
                    loading={loading}
                />
            </div>
        </>);
    }, [showAsPercentages, noOfMonthsToShow, loading]);

    const StorageTable = useCallback(({ title, data, loadingData }) => {

        const tableAreaHeaders = ['Area', 'Documents', 'Total Size', '% of Total'];

        return (
            <div className='spacer-top'>
                <h3>{title}</h3>
                <ListingTable
                    id='storage-area-table'
                    headers={tableAreaHeaders}
                    data={data}
                    totalRecordCount={data.length}
                    pageable={false}
                    showPerPage={false}
                    shrinkLastColumn={false}
                    loadingData={loadingData}
                    columnWidths={['40%', '20%', '27%', '13%']}
                    noDataHeading="No storage used this month"
                />
            </div>
        )
    }, []);

    return (
        <>
            <div className='spacer-top'>
                <MultiStackedBarChart data={chartData}
                    tooltipFormatter={tooltipFormatter}
                    loading={loading}
                    xAxisName='Months'
                    yAxisName={`Storage (${showAsPercentages ? '%' : 'MB'})`}
                    title="Monthly Storage Usage"
                    controls={controlPanel()}
                />
            </div>
            <div>
                {chartData && chartData.slice().reverse().map(row => (
                    row.areas.length > 0 &&
                    <StorageTable key={row.x} title={row.x} data={row.areas} loadingData={false} />
                ))}
            </div>
        </>
    );
}

MonthlyStorage.propTypes = {
    monthlyStorageData: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    noOfMonthsToShow: PropTypes.number,
};
